import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

export default function OwnerReview() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api
      .get("/orders/orphaned")
      .then((r) => setRows(r.data))
      .catch((e) => {
        toast.error(apiError(e));
        setRows([]);
      });
  }, []);

  return (
    <div className="p-4 pb-24" data-testid="owner-review-page">
      <button
        onClick={() => navigate(-1)}
        className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">Owner Review — Deleted Batches</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Read-only. Orders below reference a PO Batch that no longer exists. Nothing here is relinked, restored or deleted
        automatically — invoices, payments, customer credits and audit history stay exactly as they are.
      </p>

      {rows === null ? (
        <div className="grid place-items-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="owner-review-empty" className="mt-6 rounded-md border border-dashed py-16 text-center text-sm text-muted-foreground">
          No orders reference a deleted PO Batch.
        </div>
      ) : (
        <div className="mt-4 space-y-2" data-testid="owner-review-list">
          {rows.map((o) => (
            <button
              key={o.id}
              data-testid={`owner-review-order-${o.id}`}
              onClick={() => navigate(`/orders/${o.id}`)}
              className="flex w-full items-start gap-3 rounded-md border border-amber-200 bg-amber-50/60 p-3 text-left active:scale-[0.99]"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">{o.customer_name}</p>
                <p className="text-xs text-muted-foreground">
                  Deleted batch: {o.batch_name || "(unknown)"} · #{o.id.slice(0, 8)} · {o.status}
                </p>
                <p className="mt-0.5 text-[11px] font-semibold text-stone-600">
                  {o.active_item_count} active item{o.active_item_count === 1 ? "" : "s"} · {o.invoice_count} invoice
                  {o.invoice_count === 1 ? "" : "s"} · {o.payment_count} payment{o.payment_count === 1 ? "" : "s"} preserved
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <div className="mt-4 rounded-md border bg-card p-4 text-xs text-muted-foreground" data-testid="owner-review-guidance">
          <p className="mb-1 font-bold text-foreground">Safe options (manual, Owner decision only)</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Leave as-is — financial history remains intact and reportable.</li>
            <li>Create a fresh order in a valid active batch for any items still to be purchased.</li>
            <li>Cancel the remaining active items on the affected order so they no longer appear outstanding.</li>
          </ul>
          <p className="mt-2">Master Products are never affected by batch deletion and remain reusable in new batches.</p>
        </div>
      )}
    </div>
  );
}
